import React, { useState, useEffect, useRef } from 'react';

// This is our new, powerful modal for editing multiple boxes on a single page.
export default function MultiCropEditorModal({ isOpen, onClose, onSave, page, pageNumbers = [], onNavigate }) {
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const [boxes, setBoxes] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const dragInfo = useRef({ isDragging: false, isResizing: false, handle: '', startPos: {x: 0, y: 0} });

    // This effect initializes the boxes when the modal opens
    useEffect(() => {
        if (isOpen && page && imageRef.current) {
            const image = imageRef.current;
            const canvas = canvasRef.current;
            if (!image || !canvas) return;
            
            const setupCanvasAndBoxes = () => {
                canvas.width = image.clientWidth;
                canvas.height = image.clientHeight;
                const scaleX = canvas.width / image.naturalWidth;
                const scaleY = canvas.height / image.naturalHeight;

                const initialBoxes = page.offerBlocks
                    .filter(block => block.box_2d && Array.isArray(block.box_2d) && block.box_2d.length === 4)
                    .map(block => ({
                        x: (block.box_2d[1] / 1000) * image.naturalWidth * scaleX,
                        y: (block.box_2d[0] / 1000) * image.naturalHeight * scaleY,
                        width: ((block.box_2d[3] - block.box_2d[1]) / 1000) * image.naturalWidth * scaleX,
                        height: ((block.box_2d[2] - block.box_2d[0]) / 1000) * image.naturalHeight * scaleY,
                    }));
                setBoxes(initialBoxes);
            };

            if (image.complete) {
                setupCanvasAndBoxes();
            } else {
                image.onload = setupCanvasAndBoxes;
            }
        }
    }, [isOpen, page]);

    // This effect redraws the canvas whenever the boxes change
    useEffect(() => {
        if (isOpen && canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
            boxes.forEach((box, index) => drawBox(ctx, box, index === activeIndex));
        }
    }, [boxes, activeIndex, isOpen]);

    const drawBox = (ctx, rect, isActive) => {
        ctx.strokeStyle = isActive ? '#ef4444' : '#3b82f6';
        ctx.lineWidth = isActive ? 3 : 2;
        ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
        // Draw resize handles if the box is active
        if (isActive) {
            ctx.fillStyle = '#ef4444';
            const handleSize = 10;
            const handles = [
                { x: rect.x - handleSize/2, y: rect.y - handleSize/2 }, 
                { x: rect.x + rect.width - handleSize/2, y: rect.y - handleSize/2 },
                { x: rect.x - handleSize/2, y: rect.y + rect.height - handleSize/2 }, 
                { x: rect.x + rect.width - handleSize/2, y: rect.y + rect.height - handleSize/2 }
            ];
            handles.forEach(h => ctx.fillRect(h.x, h.y, handleSize, handleSize));
        }
    };

    const handleMouseDown = (e) => {
        if (!e) {
            console.error("handleMouseDown called without an event object!");
            return; // Stop if no event
        }
        const canvas = canvasRef.current;
        if (!canvas) {
            console.error("handleMouseDown called before canvas ref was attached!");
            return; // Stop if no canvas
        }
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        dragInfo.current.startPos = { x, y };

        // Check if clicking on a resize handle of the active box
        if (activeIndex !== -1) {
            const activeBox = boxes[activeIndex];
            const handleSize = 10;
            if (x > activeBox.x - handleSize && x < activeBox.x + handleSize && y > activeBox.y - handleSize && y < activeBox.y + handleSize) { dragInfo.current = { ...dragInfo.current, isResizing: true, handle: 'top-left' }; return; }
            if (x > activeBox.x + activeBox.width - handleSize && x < activeBox.x + activeBox.width + handleSize && y > activeBox.y - handleSize && y < activeBox.y + handleSize) { dragInfo.current = { ...dragInfo.current, isResizing: true, handle: 'top-right' }; return; }
            if (x > activeBox.x - handleSize && x < activeBox.x + handleSize && y > activeBox.y + activeBox.height - handleSize && y < activeBox.y + activeBox.height + handleSize) { dragInfo.current = { ...dragInfo.current, isResizing: true, handle: 'bottom-left' }; return; }
            if (x > activeBox.x + activeBox.width - handleSize && x < activeBox.x + activeBox.width + handleSize && y > activeBox.y + activeBox.height - handleSize && y < activeBox.y + activeBox.height + handleSize) { dragInfo.current = { ...dragInfo.current, isResizing: true, handle: 'bottom-right' }; return; }
        }

        // Check if clicking inside any box to make it active and start dragging
        for (let i = boxes.length - 1; i >= 0; i--) {
            const box = boxes[i];
            if (x > box.x && x < box.x + box.width && y > box.y && y < box.y + box.height) {
                setActiveIndex(i);
                dragInfo.current = { ...dragInfo.current, isDragging: true };
                return;
            }
        }

        // If clicking on empty space, deselect all boxes
        setActiveIndex(-1);
    };

    const handleMouseMove = (e) => {
        if (activeIndex === -1 || (!dragInfo.current.isDragging && !dragInfo.current.isResizing)) return;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const dx = x - dragInfo.current.startPos.x;
        const dy = y - dragInfo.current.startPos.y;

        const newBoxes = [...boxes];
        let newRect = { ...newBoxes[activeIndex] };

        if (dragInfo.current.isDragging) {
            newRect.x += dx;
            newRect.y += dy;
        } else if (dragInfo.current.isResizing) {
            switch (dragInfo.current.handle) {
                case 'top-left': newRect.x += dx; newRect.y += dy; newRect.width -= dx; newRect.height -= dy; break;
                case 'top-right': newRect.y += dy; newRect.width += dx; newRect.height -= dy; break;
                case 'bottom-left': newRect.x += dx; newRect.width -= dx; newRect.height += dy; break;
                case 'bottom-right': newRect.width += dx; newRect.height += dy; break;
                default: break;
            }
        }
        
        newBoxes[activeIndex] = newRect;
        setBoxes(newBoxes);
        dragInfo.current.startPos = { x, y };
    };
    
    const handleMouseUp = () => {
        dragInfo.current = { isDragging: false, isResizing: false, handle: '', startPos: {x: 0, y: 0} };
    };

    const handleAddNewBox = () => {
        const newBox = { x: 10, y: 10, width: 150, height: 150 };
        setBoxes(prev => [...prev, newBox]);
        setActiveIndex(boxes.length); // Make the new box active
    };

    const handleDeleteBox = () => {
        if (activeIndex !== -1 && window.confirm('Are you sure you want to delete this box?')) {
            const newBoxes = boxes.filter((_, index) => index !== activeIndex);
            setBoxes(newBoxes);
            setActiveIndex(-1); // Deselect
        }
    };
    
    const getCurrentBox2dList = () => {
        const image = imageRef.current;
        const canvas = canvasRef.current;
        if (!image || !canvas) return null;

        const scaleX = image.naturalWidth / canvas.width;
        const scaleY = image.naturalHeight / canvas.height;

        return boxes.map(box => [
            (box.y * scaleY) * 1000 / image.naturalHeight,
            (box.x * scaleX) * 1000 / image.naturalWidth,
            ((box.y + box.height) * scaleY) * 1000 / image.naturalHeight,
            ((box.x + box.width) * scaleX) * 1000 / image.naturalWidth
        ]);
    };

    const handleSave = () => {
        const newBox2dList = getCurrentBox2dList();
        if (newBox2dList) onSave(page.pageNumber, newBox2dList);
        onClose();
    };

    // Navigation: save current page, then switch
    const currentPageIndex = pageNumbers.indexOf(page?.pageNumber);
    const hasPrev = currentPageIndex > 0;
    const hasNext = currentPageIndex < pageNumbers.length - 1;

    const handleNavigate = (direction) => {
        if (!onNavigate) return;
        const newBox2dList = getCurrentBox2dList();
        if (newBox2dList) onSave(page.pageNumber, newBox2dList);

        const targetIndex = currentPageIndex + direction;
        if (targetIndex >= 0 && targetIndex < pageNumbers.length) {
            onNavigate(pageNumbers[targetIndex]);
        }
    };

    if (!isOpen || !page) return null;
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg shadow-xl flex flex-col max-w-[95vw] max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        {pageNumbers.length > 1 && (
                            <button
                                onClick={() => handleNavigate(-1)}
                                disabled={!hasPrev}
                                className="bg-gray-200 text-gray-700 font-bold py-2 px-3 rounded-lg hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed text-lg"
                                title="Previous page"
                            >
                                &larr;
                            </button>
                        )}
                        <h2 className="text-xl font-bold">Editing Offers for Page {page.pageNumber}</h2>
                        {pageNumbers.length > 1 && (
                            <button
                                onClick={() => handleNavigate(1)}
                                disabled={!hasNext}
                                className="bg-gray-200 text-gray-700 font-bold py-2 px-3 rounded-lg hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed text-lg"
                                title="Next page"
                            >
                                &rarr;
                            </button>
                        )}
                    </div>
                    <div>
                        <button onClick={handleAddNewBox} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 mr-4">+ Add Box</button>
                        <button onClick={handleDeleteBox} disabled={activeIndex === -1} className="bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 disabled:bg-gray-400">Delete Selected Box</button>
                    </div>
                </div>

                <div className="relative">
                    <img
                        ref={imageRef}
                        src={page.imageDataUrl}
                        className="max-w-full max-h-[70vh] block"
                        alt={`Flyer page ${page.pageNumber}`}
                    />
                    <canvas
                        ref={canvasRef}
                        className="absolute top-0 left-0"
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    />
                </div>

                <div className="flex justify-end gap-4 mt-4">
                    <button onClick={onClose} className="bg-gray-300 text-gray-800 font-bold py-2 px-6 rounded-lg hover:bg-gray-400">Cancel</button>
                    <button onClick={handleSave} className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700">Save Changes</button>
                </div>
            </div>
        </div>
    );
};